<template>
	<div class="ContentPreviewEdit" style="height: 100%;">
		<Head :hasBack="true" @back="close()">
			<div class="heading">
				<h1>
					Content Preview
				</h1>
			</div>
			<div class="actions">
				<ActionButton class="delete" @click="del()" v-if="id != 'new'">Delete</ActionButton>
				<ActionButton class="save" @click="saveAndClose()">Save</ActionButton>
			</div>
		</Head>
		<div class="body" v-if="preview" style="display: flex; height: 100%;">
			<div class="main" style="margin: 30px; flex: 1 1 0;">
				<label for="name">Name (required)</label>
				<input type="text" class="input" id="name" v-model="preview.name" />

				<label for="url">URL (required)</label>
				<input type="text" class="input" id="url" v-model="preview.baseUrl" />
				<Info>
					JAMES applications typically have a "/preview" in their preview url, make sure to include this with the specified URL!<br />
					<br />
					JAMES supports a locale in the path, you may specify the locale as a placeholder in the URL with "{locale}".
					Example: https://my-james.com/{locale}/previews
				</Info>

				<label style="margin-top: 30px;">Custom URL patterns (optional)</label>
				<Info>
					By default JAMES resolves preview URLs automatically per content type from the base URL above.
					Add an override to set an explicit URL pattern for one or more specific content types.<br />
					<br />
					Placeholders: {baseUrl}, {entryId}, {locale}, {contentType}.
				</Info>
				<div v-for="(group, i) in configGroups" :key="i" class="configGroup">
					<div class="configGroupHead">
						<span>URL pattern override {{ i + 1 }}</span>
						<mdi close @click="removeGroup(i)" title="remove override" />
					</div>
					<div class="pickerWrap">
						<MultiTypePicker v-model="group.contentTypes" />
					</div>
					<label>URL pattern</label>
					<input type="text" class="input" v-model="group.url" placeholder="{baseUrl}/pages/{entryId}" />
				</div>
				<ActionButton class="add" style="margin-top: 15px;" @click="addGroup()">Add URL pattern</ActionButton>
			</div>
			<Sidebar>
				<div class="section">
					<h2>Which URL?</h2>
					<p>
						JAMES supports previews for some defined types.
						The URL points to a JAMES environment that is connected to this environment.
					</p>
					<p>
						Previews are per content type. Not all content types have previews.
						When editing an object, click the preview button in the sidebar to open the preview.
					</p>
				</div>
			</Sidebar>
		</div>
	</div>
</template>

<script>
import Sidebar from './Sidebar.vue'
import Head from './Head.vue'
import ActionButton from '../components/ActionButton.vue'
import Info from './Info.vue'
import MultiTypePicker from '../components/MultiTypePicker.vue'

export default {
	name: 'ContentPreviewEdit',
	components: { Sidebar, Head, ActionButton, Info, MultiTypePicker },
	inject: [ 'base', 'endpoint' ],
	data: () => ({
		id: null,
		preview: null,
		// editing model for the optional per-content-type overrides, grouped by shared URL pattern.
		// this is flattened into preview.configurations on save (one entry per content type).
		configGroups: [],
	}),
	methods: {
		async load() {
			if (this.id == 'new') {
				this.preview = { description: '', configurations: [], sys: {} }
			}
			else {
				this.preview = await this.$httpGet(this.endpoint + '/preview_environments/' + this.id)
			}
			this.configGroups = this.configurationsToGroups(this.preview.configurations)
		},
		// group the flat configurations array into { contentTypes: [], url } rows, sharing a URL pattern
		configurationsToGroups(configurations) {
			const groups = []
			const byUrl = {}
			for (const c of configurations ?? []) {
				if (!c || !c.contentType) continue
				const key = c.url ?? ''
				if (!byUrl[key]) {
					byUrl[key] = { contentTypes: [], url: c.url ?? '' }
					groups.push(byUrl[key])
				}
				byUrl[key].contentTypes.push(c.contentType)
			}
			return groups
		},
		// flatten the editing rows back into the flat configurations array (one entry per content type)
		groupsToConfigurations(groups) {
			const configurations = []
			for (const g of groups) {
				const url = (g.url ?? '').trim()
				if (!url) continue
				for (const contentType of g.contentTypes ?? []) {
					configurations.push({ url, contentType, enabled: true, example: false })
				}
			}
			return configurations
		},
		addGroup() {
			this.configGroups.push({ contentTypes: [], url: '' })
		},
		removeGroup(i) {
			this.configGroups.splice(i, 1)
		},
		async save() {
			this.preview.configurations = this.groupsToConfigurations(this.configGroups)
			if (this.id == 'new') {
				await this.$httpPost(this.endpoint + '/preview_environments/', this.preview)
				this.load()
				return
			}
			const preview = await this.$httpPut(this.endpoint + '/preview_environments/' + this.preview.sys.id, this.preview)
			//this.preview = previews.item
		},
		close() {
			this.$router.push(this.base + '/settings/previews')
		},
		async del() {
			if (!confirm('Are you sure you want to delete this preview?')) return
			await this.$httpDelete(this.endpoint + '/preview_environments/' + this.preview.sys.id)
			this.close()
		},
		async saveAndClose() {
			await this.save()
			this.close()
		},
	},
	async mounted() {
		this.id = this.$route.params.id
		document.title = 'Preview Edit'
		await this.load()
	},
}
</script>

<style scoped>
label { font-size: 14px; display: block; margin-top: 10px; margin-bottom: 4px; }
.configGroup { border: 1px solid #ddd; border-radius: 6px; padding: 12px 16px; margin-top: 12px; }
.configGroupHead { display: flex; align-items: center; justify-content: space-between; font-weight: 600; font-size: 14px; }
.configGroupHead .mdi { cursor: pointer; }
/* MultiTypePicker's inner Field carries a fixed top margin; pull it up so it sits tight in the card */
.pickerWrap { margin-top: -40px; }
</style>
